import {
	Badge,
	Button,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@monbits/ui";
import { ChevronRight } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CollapseString from "src/components/collapse-string/collapse-string";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import DrawerForm from "src/components/drawer-form/drawer-form";
import WithdrawForm, {
	type WithdrawFormHandle,
} from "src/components/withdraw-form/withdraw-form";
import addressQueries from "src/queries/address-queries";
import balancesQueries from "src/queries/balances-queries";
import {
	CURRENCY_SYMBOL,
	type MerchantBalance,
	type WithdrawalCreateRequest,
} from "src/types";
import { formatCryptoAmount, splitTicker } from "src/utils/format";

export function BalancesList() {
	const navigate = useNavigate();
	const listBalances = balancesQueries.listBalances();
	const createWithdrawal = balancesQueries.createWithdrawal();
	const listAddresses = addressQueries.listAddresses();
	const addresses = listAddresses.data || [];
	const [balances, setBalances] = React.useState<MerchantBalance[]>(
		listBalances.data?.pages[0] || [],
	);

	const [activeWithdrawal, setActiveWithdrawal] = React.useState<
		MerchantBalance[]
	>([]);
	const [isWithdrawDrawerOpen, setIsWithdrawDrawerOpen] = React.useState(false);
	const [isFormSubmitting, setIsFormSubmitting] =
		React.useState<boolean>(false);
	const withdrawFormRef = React.useRef<WithdrawFormHandle | null>(null);

	const openNotification = (title: string, description: string) => {
		toast.success(title, {
			description,
		});
	};

	const isLoadingBalance = listBalances.isPending || listBalances.isFetching;

	React.useEffect(() => {
		setBalances(listBalances.data?.pages[0] || []);
	}, [listBalances.data]);

	const withdrawFund = async (value: WithdrawalCreateRequest) => {
		try {
			setIsFormSubmitting(true);

			// Find the selected address to get the actual blockchain address
			const selectedAddress = addresses.find(
				(addr) => addr.id === value.addressId,
			);
			if (!selectedAddress) {
				throw new Error("Selected address not found");
			}

			// Find the balance to get its UUID
			const selectedBalance = balances.find(
				(bal) => bal.id === value.balanceId,
			);
			if (!selectedBalance) {
				throw new Error("Selected balance not found");
			}

			// Convert to API format
			const apiRequest = {
				balanceId: selectedBalance.id,
				recipientAddress: selectedAddress.address,
				amount: value.amount,
				deductFeeFromAmount: value.deductFeeFromAmount,
			};

			await createWithdrawal.mutateAsync(apiRequest);
			setActiveWithdrawal([]);
			setIsWithdrawDrawerOpen(false);
			openNotification(
				"Withdrawal created",
				`You have successfully created a new withdrawal with total amount of ${value.amount} ${selectedBalance.ticker}`,
			);
		} catch (error: unknown) {
			console.error("error occurred: ", error);

			// Parse and display API validation errors
			const axiosError = error as {
				response?: {
					data?: { errors?: { message: string }[]; message?: string };
				};
				message?: string;
			};
			if (
				axiosError?.response?.data?.errors &&
				Array.isArray(axiosError.response.data.errors)
			) {
				const errorMessages = axiosError.response.data.errors
					.map((err) => err.message)
					.join(", ");
				toast.error("Withdrawal failed", {
					description: errorMessages,
				});
			} else if (axiosError?.response?.data?.message) {
				toast.error("Withdrawal failed", {
					description: axiosError.response.data.message,
				});
			} else if (axiosError?.message) {
				toast.error("Withdrawal failed", {
					description: axiosError.message,
				});
			} else {
				toast.error("Withdrawal failed", {
					description: "An unexpected error occurred. Please try again.",
				});
			}
		} finally {
			setIsFormSubmitting(false);
		}
	};

	if (isLoadingBalance) {
		return (
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Network</TableHead>
						<TableHead className="w-[150px]">Currency</TableHead>
						<TableHead>Balance</TableHead>
						<TableHead>USD Balance</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{Array.from({ length: 5 }).map((_, index) => (
						<TableRow key={index}>
							<TableCell>
								<div className="flex items-center gap-2">
									<Skeleton className="h-5 w-5 rounded-full" />
									<Skeleton className="h-5 w-24" />
								</div>
							</TableCell>
							<TableCell>
								<div className="flex items-center gap-2">
									<Skeleton className="h-5 w-5 rounded-full" />
									<Skeleton className="h-5 w-16" />
								</div>
							</TableCell>
							<TableCell>
								<Skeleton className="h-5 w-28" />
							</TableCell>
							<TableCell>
								<div className="flex items-center justify-between">
									<Skeleton className="h-5 w-20" />
									<Skeleton className="h-8 w-32" />
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		);
	}

	if (balances.length === 0) {
		return (
			<div className="flex flex-col items-center gap-4 py-16 px-8 text-center">
				<div className="space-y-2 max-w-md">
					<h3 className="text-base font-medium">No balances yet</h3>
					<p className="text-sm text-muted-foreground">
						Balances appear here after you receive your first payment.
					</p>
				</div>
				<div className="flex flex-wrap items-center justify-center gap-2">
					<Button onClick={() => navigate("/payment-links")}>
						Create a payment link
					</Button>
					<Button variant="ghost" onClick={() => navigate("/payments")}>
						Go to payments
					</Button>
				</div>
			</div>
		);
	}

	return (
		<>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Network</TableHead>
						<TableHead className="w-[150px]">Currency</TableHead>
						<TableHead>Balance</TableHead>
						<TableHead>USD Balance</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{balances.map((record) => (
						<TableRow key={record.id}>
							<TableCell>
								<div className="flex items-center gap-2">
									<CryptoIcon ticker={record.blockchain} className="h-5 w-5" />
									<span className="whitespace-nowrap">
										{record.blockchainName}
									</span>
								</div>
							</TableCell>
							<TableCell>
								<div className="flex items-center gap-2">
									<CryptoIcon ticker={record.ticker} className="h-5 w-5" />
									<span className="whitespace-nowrap">{record.currency}</span>
								</div>
							</TableCell>
							<TableCell className="font-mono">
								<CollapseString
									style={{ marginRight: "10px" }}
									text={`${formatCryptoAmount(record.amount, { ticker: record.ticker, maxDecimals: 6 })} ${splitTicker(record.ticker).token || splitTicker(record.ticker).blockchain}`}
									collapseAt={12}
									withPopover
								/>
							</TableCell>
							<TableCell>
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2 font-mono">
										<CollapseString
											style={{ marginRight: "10px" }}
											text={`${CURRENCY_SYMBOL.USD}${record.usdAmount}`}
											collapseAt={12}
											withPopover
										/>
										{record.isTest && (
											<Badge
												variant="outline"
												className="bg-(--warning-bg) text-(--warning-fg) border-(--warning-border)"
											>
												Test Balance
											</Badge>
										)}
									</div>
									<Button
										variant="outline"
										size="sm"
										onClick={() => {
											setActiveWithdrawal([record]);
											setIsWithdrawDrawerOpen(true);
										}}
									>
										Withdraw funds
										<ChevronRight className="ml-2 h-4 w-4" />
									</Button>
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>

			<DrawerForm
				title="Withdraw funds"
				description="Withdraw your crypto balance to an external address."
				isOpen={isWithdrawDrawerOpen}
				onClose={() => setIsWithdrawDrawerOpen(false)}
				onSubmit={() => withdrawFormRef.current?.submit()}
				isSubmitting={isFormSubmitting}
				submitText="Create Withdrawal"
				width={512}
			>
				{activeWithdrawal[0] && (
					<WithdrawForm
						ref={withdrawFormRef}
						onCancel={() => {
							setActiveWithdrawal([]);
						}}
						onFinish={withdrawFund}
						balance={activeWithdrawal[0]}
						addresses={addresses}
						openPopupFunc={openNotification}
						isFormSubmitting={isFormSubmitting}
					/>
				)}
			</DrawerForm>
		</>
	);
}
